<script setup lang="ts">
import { ref, computed, Ref } from 'vue'
import { useI18n } from 'vue-i18n'
import yup from '@/plugins/yup'
import { useSensorStore } from '@/store/sensor'
import { IDoorOpenAlarmSensor } from '@/interfaces/entities/alarm_profile'
import { InputFromField } from '@/interfaces/components/inputFromField'
import InputGroup from '@/components/Auths/InputGroup.vue'
import ButtonLoading from '@/components/Buttons/ButtonLoading.vue'
import { ValidationError } from 'yup'
import { useErrorStore } from '@/store/error'
import OutlineButton from '@/components/Globals/Buttons/OutlineButton.vue'
import { useAlarmProfileStore } from '@/store/alarmProfile'

const { t } = useI18n()
const profileStore = useAlarmProfileStore()
const isLoading = ref(false)

const emit = defineEmits(['cancel'])

const props = defineProps<{
  currentProfile: IDoorOpenAlarmSensor | null,
  formType: 'create' | 'edit',
}>()

const currentSettings = props.currentProfile?.settings

const inputs: Ref<InputFromField[]> = ref([
  {
    label: 'Nome',
    placeholder: 'alarms.inform_profile_name',
    value: props.formType === 'edit' ? props.currentProfile?.name ?? '' : '',
  },
  {
    label: 'alarms.door_open.max_door_open_time_setpoint',
    placeholder: 'alarms.inform_time.door_open_max_time',
    value: props.formType === 'edit' ? currentSettings?.max_door_open_time_setpoint ?? '' : '',
  },
  {
    label: 'alarms.door_open.debounce_time_setpoint',
    placeholder: 'alarms.inform_time.minutes',
    value: props.formType === 'edit' ? currentSettings?.debounce_time_setpoint ?? '' : '',
  },
])

const doorOpenSettingsSchema = yup.object({
  name: yup
    .string()
    .required('O campo nome é obrigatório'),
  max_door_open_time_setpoint: yup
    .number()
    .typeError('Informe um número válido para o tempo máximo da porta aberta')
    .required('Tempo máximo da porta aberta é obrigatório'),
  debounce_time_setpoint: yup.number().typeError('Informe um número válido para o tempo de debounce').required('Tempo de debounce é obrigatório'),
})


async function handleSubmit() {
  const values: IDoorOpenAlarmSensor = {
    name: String(inputs.value[0].value),
    max_door_open_time_setpoint: inputs.value[1].value,
    debounce_time_setpoint: inputs.value[2].value,
  }

  try {
    await doorOpenSettingsSchema.validate(values, { abortEarly: false })
    isLoading.value = true
    if(props.formType === 'create'){
      await profileStore.createDoorOpenAlarmProfile(values)
    } else if(props.formType === 'edit' && props.currentProfile){
      await profileStore.editDoorOpenAlarmProfile(props.currentProfile.id, values)
    }
    isLoading.value = false
    emit('cancel')
  } catch (err) {
    if (err instanceof ValidationError) {
      useErrorStore().clearErrors();
      useErrorStore().setErrors('Erro ao criar perfil: ', err.errors)
    }
  }
}
</script>

<template>
  <div class="border border-stroke bg-gray-2 p-5 shadow-sm">
    <div class="flex flex-wrap gap-4">
      <span v-if="props.formType ==='create'" class="w-full text-base font-medium text-gray-500 mb-1">
        {{ t('alarms.door_open.create_profile') }}
      </span>
      <div v-if="props.formType === 'edit'" class="w-full flex justify-items-center">
        <span class="w-full text-base font-medium text-gray-500 mb-1">
          {{ "Editar perfil: " + (props.currentProfile?.name || '') }}
        </span>
      </div>
      <div class="w-full border-t border-stroke pt-1"></div>
      <template v-for="(input, index) in inputs" :key="index">
        <div class="min-w-[200px] w-full">
          <InputGroup v-model="input.value" :label="t(input.label)" :placeholder="t(input.placeholder)" />
        </div>
      </template>

      <div class="flex w-full justify-end gap-2">
        <OutlineButton 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !border-stroke !bg-white !shadow-sm hover:!bg-gray-2 !flex !items-center !justify-center whitespace-nowrap"
          @click="emit('cancel')"
          label="Cancelar" />
        <ButtonLoading 
          class="!h-auto w-32 !py-2 !text-sm !px-2 !text-white !border-blue-600 !bg-blue-600 !shadow-sm hover:!bg-blue-700 !flex !items-center !justify-center whitespace-nowrap"
          :isLoading="isLoading"
          @submit="handleSubmit"
          label="Salvar" />
      </div>
    </div>
  </div>
</template>

<style scoped>
  :deep(label) {
    color: #64748b;
    font-size: 0.875rem;
  }
  :deep(input) {
    padding-top: 0.5rem;
    padding-bottom: 0.5rem;
  }
</style>

